"use client";

import React, { useState } from "react";
import { ChevronUp, Plus, X, Edit, Image as ImageIcon } from "lucide-react";
import Button from "./Masters/Button";
import RTE from "./RTE";

interface TourCategoryRow {
  id: string;
  tourCategory: string;
  city: string;
  hotelCategory: string;
  hotel: string;
  roomCategory: string;
  noOfNight: string;
  single: string;
  twinSharing: string;
  extraBed: string;
  childNoBed: string;
}

interface TransportRow {
  id: string;
  transportType: string;
  vehicleType: string;
  serviceType: string;
  city: string;
  description: string;
  cost: string;
  markup: string;
}

interface SightseeingRow {
  id: string;
  sightseeing: string;
  duration: string;
  thumb: string;
  description: string;
  adultPrice: string;
  childPrice: string;
  markup: string;
}

interface FlightRow {
  id: string;
  fromSector: string;
  toSector: string;
  airline: string;
  description: string;
  adultPrice: string;
  childPrice: string;
  markup: string;
}

interface StandardPricingRow {
  id: string;
  costName: string;
  markupType: string;
  markupAmount: string;
  singleOccupancySupplementCost: string;
  costPerAdultExtraBed: string;
  costPerChildExtraBed: string;
  costPerChildWoBed: string;
}

const PricePaymentInformation: React.FC = () => {
  const [startCity, setStartCity] = useState("");
  const [markupType, setMarkupType] = useState<"percent" | "fixed">("percent");
  const [markupAmount, setMarkupAmount] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");

  // Tour Category
  const [tourCategories, setTourCategories] = useState<TourCategoryRow[]>([
    {
      id: "1",
      tourCategory: "Standard",
      city: "",
      hotelCategory: "",
      hotel: "",
      roomCategory: "",
      noOfNight: "",
      single: "",
      twinSharing: "",
      extraBed: "",
      childNoBed: "",
    },
  ]);

  // Transport
  const [transports, setTransports] = useState<TransportRow[]>([
    {
      id: "1",
      transportType: "Cab",
      vehicleType: "",
      serviceType: "",
      city: "",
      description: "",
      cost: "",
      markup: "",
    },
  ]);

  // Sightseeing
  const [sightseeings, setSightseeings] = useState<SightseeingRow[]>([
    {
      id: "1",
      sightseeing: "India Gate",
      duration: "",
      thumb: "",
      description: "",
      adultPrice: "",
      childPrice: "",
      markup: "",
    },
  ]);

  // Flights
  const [flights, setFlights] = useState<FlightRow[]>([
    {
      id: "1",
      fromSector: "",
      toSector: "",
      airline: "",
      description: "",
      adultPrice: "",
      childPrice: "",
      markup: "",
    },
  ]);

  // Standard Pricing Tables
  const [standardPricing1, setStandardPricing1] = useState<StandardPricingRow[]>([
    {
      id: "1",
      costName: "Cost Price",
      markupType: "%",
      markupAmount: "10",
      singleOccupancySupplementCost: "₹5000",
      costPerAdultExtraBed: "₹5000",
      costPerChildExtraBed: "₹5000",
      costPerChildWoBed: "₹5000",
    },
  ]);

  const [standardPricing2, setStandardPricing2] = useState<StandardPricingRow[]>([
    {
      id: "1",
      costName: "Cost Price",
      markupType: "%",
      markupAmount: "10",
      singleOccupancySupplementCost: "₹5000",
      costPerAdultExtraBed: "₹5000",
      costPerChildExtraBed: "₹5000",
      costPerChildWoBed: "₹5000",
    },
  ]);

  // Add Price Section
  const [adultSingleCost, setAdultSingleCost] = useState("");
  const [adultSingleSelling, setAdultSingleSelling] = useState("");
  const [extraBedCost, setExtraBedCost] = useState("");
  const [extraBedSelling, setExtraBedSelling] = useState("");
  const [adultTwinCost, setAdultTwinCost] = useState("");
  const [adultTwinSelling, setAdultTwinSelling] = useState("");
  const [childNoBedCost, setChildNoBedCost] = useState("");
  const [childNoBedSelling, setChildNoBedSelling] = useState("");
  const [priceType, setPriceType] = useState("");

  const handleAddTourCategory = () => {
    setTourCategories([
      ...tourCategories,
      {
        id: Date.now().toString(),
        tourCategory: "Standard",
        city: "",
        hotelCategory: "",
        hotel: "",
        roomCategory: "",
        noOfNight: "",
        single: "",
        twinSharing: "",
        extraBed: "",
        childNoBed: "",
      },
    ]);
  };

  const handleAddTransport = () => {
    setTransports([
      ...transports,
      {
        id: Date.now().toString(),
        transportType: "Cab",
        vehicleType: "",
        serviceType: "",
        city: "",
        description: "",
        cost: "",
        markup: "",
      },
    ]);
  };

  const handleAddSightseeing = () => {
    setSightseeings([
      ...sightseeings,
      {
        id: Date.now().toString(),
        sightseeing: "",
        duration: "",
        thumb: "",
        description: "",
        adultPrice: "",
        childPrice: "",
        markup: "",
      },
    ]);
  };

  const handleAddFlight = () => {
    setFlights([
      ...flights,
      {
        id: Date.now().toString(),
        fromSector: "",
        toSector: "",
        airline: "",
        description: "",
        adultPrice: "",
        childPrice: "",
        markup: "",
      },
    ]);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-800">
          Price and Payment information
        </h2>
        <button
          type="button"
          className="p-1 bg-gray-100 rounded hover:bg-gray-200"
          title="Collapse"
        >
          <ChevronUp className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Start City and Markup Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Start City
            </label>
            <textarea
              rows={4}
              value={startCity}
              onChange={(e) => setStartCity(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
            />
            <Button
              size="sm"
              variant="danger"
              onClick={() => console.log("Add Other City")}
              icon={<Plus className="w-4 h-4" />}
              iconPosition="left"
              className="mt-2"
            >
              Add Other City
            </Button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Markup Type
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="markupType"
                    checked={markupType === "percent"}
                    onChange={() => setMarkupType("percent")}
                    className="h-4 w-4"
                  />
                  <span className="text-[11px] text-gray-700">Percent (%)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="markupType"
                    checked={markupType === "fixed"}
                    onChange={() => setMarkupType("fixed")}
                    className="h-4 w-4"
                  />
                  <span className="text-[11px] text-gray-700">Fixed</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Markup Amount
              </label>
              <input
                type="text"
                value={markupAmount}
                onChange={(e) => setMarkupAmount(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tour Category Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Tour Category
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b">Tour Category</th>
                <th className="px-3 py-2 text-left border-b">City</th>
                <th className="px-3 py-2 text-left border-b">Hotel Category</th>
                <th className="px-3 py-2 text-left border-b">Hotel</th>
                <th className="px-3 py-2 text-left border-b">Room Category</th>
                <th className="px-3 py-2 text-left border-b">No of Night</th>
                <th className="px-3 py-2 text-left border-b">Single</th>
                <th className="px-3 py-2 text-left border-b">Twin Sharing</th>
                <th className="px-3 py-2 text-left border-b">Extra bed</th>
                <th className="px-3 py-2 text-left border-b">Child no bed</th>
              </tr>
            </thead>
            <tbody>
              {tourCategories.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2">{row.tourCategory}</td>
                  <td className="px-3 py-2">
                    <select
                      value={row.city}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id ? { ...r, city: e.target.value } : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>-- Select Tour Type --</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.hotelCategory}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, hotelCategory: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>-- Select Hotel Category --</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.hotel}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id ? { ...r, hotel: e.target.value } : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>-- Select Hotel --</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.roomCategory}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, roomCategory: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>-- Select Room Category --</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.noOfNight}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, noOfNight: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.single}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, single: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.twinSharing}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, twinSharing: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.extraBed}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, extraBed: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.childNoBed}
                      onChange={(e) =>
                        setTourCategories(
                          tourCategories.map((r) =>
                            r.id === row.id
                              ? { ...r, childNoBed: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAddTourCategory}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            className="mt-2"
          >
            Add Row
          </Button>
        </div>
      </div>

      {/* Transport Type Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Transport Type
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b">Transport Type</th>
                <th className="px-3 py-2 text-left border-b">Vehicle Type</th>
                <th className="px-3 py-2 text-left border-b">Service Type</th>
                <th className="px-3 py-2 text-left border-b">City</th>
                <th className="px-3 py-2 text-left border-b">Description</th>
                <th className="px-3 py-2 text-left border-b">Cost</th>
                <th className="px-3 py-2 text-left border-b">Markup</th>
                <th className="px-3 py-2 text-left border-b">Action</th>
              </tr>
            </thead>
            <tbody>
              {transports.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2">{row.transportType}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.vehicleType}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id
                              ? { ...r, vehicleType: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.serviceType}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id
                              ? { ...r, serviceType: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.city}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id ? { ...r, city: e.target.value } : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id
                              ? { ...r, description: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.cost}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id ? { ...r, cost: e.target.value } : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.markup}
                      onChange={(e) =>
                        setTransports(
                          transports.map((r) =>
                            r.id === row.id
                              ? { ...r, markup: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setTransports(transports.filter((r) => r.id !== row.id))
                        }
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAddTransport}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            className="mt-2"
          >
            Add Row
          </Button>
        </div>
      </div>

      {/* Sightseeing Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Sightseeing</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b">Sightseeing</th>
                <th className="px-3 py-2 text-left border-b">Duration</th>
                <th className="px-3 py-2 text-left border-b">Thumb</th>
                <th className="px-3 py-2 text-left border-b">Description</th>
                <th className="px-3 py-2 text-left border-b">Adult Price</th>
                <th className="px-3 py-2 text-left border-b">Child Price</th>
                <th className="px-3 py-2 text-left border-b">Markup</th>
                <th className="px-3 py-2 text-left border-b">Action</th>
              </tr>
            </thead>
            <tbody>
              {sightseeings.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.sightseeing}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, sightseeing: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.duration}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, duration: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`thumb-${row.id}`}
                      />
                      <label
                        htmlFor={`thumb-${row.id}`}
                        className="cursor-pointer"
                        title="Upload Thumbnail"
                      >
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      </label>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, description: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.adultPrice}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, adultPrice: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.childPrice}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, childPrice: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.markup}
                      onChange={(e) =>
                        setSightseeings(
                          sightseeings.map((r) =>
                            r.id === row.id
                              ? { ...r, markup: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setSightseeings(
                            sightseeings.filter((r) => r.id !== row.id)
                          )
                        }
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAddSightseeing}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            className="mt-2"
          >
            Add Row
          </Button>
        </div>
      </div>

      {/* From Sector Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">From Sector</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b">From Sector</th>
                <th className="px-3 py-2 text-left border-b">to Sector</th>
                <th className="px-3 py-2 text-left border-b">Airline</th>
                <th className="px-3 py-2 text-left border-b">Description</th>
                <th className="px-3 py-2 text-left border-b">Adult Price</th>
                <th className="px-3 py-2 text-left border-b">Child Price</th>
                <th className="px-3 py-2 text-left border-b">Markup</th>
                <th className="px-3 py-2 text-left border-b">Action</th>
              </tr>
            </thead>
            <tbody>
              {flights.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.fromSector}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, fromSector: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.toSector}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, toSector: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.airline}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, airline: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>--Select Airline--</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, description: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.adultPrice}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, adultPrice: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.childPrice}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, childPrice: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.markup}
                      onChange={(e) =>
                        setFlights(
                          flights.map((r) =>
                            r.id === row.id
                              ? { ...r, markup: e.target.value }
                              : r
                          )
                        )
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setFlights(flights.filter((r) => r.id !== row.id))
                        }
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAddFlight}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            className="mt-2"
          >
            Add Row
          </Button>
        </div>
      </div>

      {/* Add Price Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Add Price</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Adult single occupancy
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Cost Price
                  </label>
                  <input
                    type="text"
                    value={adultSingleCost}
                    onChange={(e) => setAdultSingleCost(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Selling Price
                  </label>
                  <input
                    type="text"
                    value={adultSingleSelling}
                    onChange={(e) => setAdultSingleSelling(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Extra bed
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Cost Price
                  </label>
                  <input
                    type="text"
                    value={extraBedCost}
                    onChange={(e) => setExtraBedCost(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Selling Price
                  </label>
                  <input
                    type="text"
                    value={extraBedSelling}
                    onChange={(e) => setExtraBedSelling(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Adult twin Shering
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Cost Price
                  </label>
                  <input
                    type="text"
                    value={adultTwinCost}
                    onChange={(e) => setAdultTwinCost(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Selling Price
                  </label>
                  <input
                    type="text"
                    value={adultTwinSelling}
                    onChange={(e) => setAdultTwinSelling(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Chield No bed
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Cost Price
                  </label>
                  <input
                    type="text"
                    value={childNoBedCost}
                    onChange={(e) => setChildNoBedCost(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    Selling Price
                  </label>
                  <input
                    type="text"
                    value={childNoBedSelling}
                    onChange={(e) => setChildNoBedSelling(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Price Type
              </label>
              <select
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option>-- Select Price Type --</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Description */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-[11px] font-medium text-gray-700 mb-2">
          Payment Description
        </label>
        <RTE
          content={paymentDescription}
          onChange={setPaymentDescription}
        />
      </div>

      {/* Standard Pricing Tables */}
      <div className="space-y-4">
        {/* First Standard Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Standard</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b">Cost Name</th>
                  <th className="px-3 py-2 text-left border-b">Markup Type</th>
                  <th className="px-3 py-2 text-left border-b">Markup Amount</th>
                  <th className="px-3 py-2 text-left border-b">
                    Single Occupancy Supplement Cost
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Adult Extra Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Child Extra Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Child W/o Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {standardPricing1.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="px-3 py-2">{row.costName}</td>
                    <td className="px-3 py-2">{row.markupType}</td>
                    <td className="px-3 py-2">{row.markupAmount}</td>
                    <td className="px-3 py-2">
                      {row.singleOccupancySupplementCost}
                    </td>
                    <td className="px-3 py-2">{row.costPerAdultExtraBed}</td>
                    <td className="px-3 py-2">{row.costPerChildExtraBed}</td>
                    <td className="px-3 py-2">{row.costPerChildWoBed}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Edit"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                          title="Add"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Second Standard Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Standard</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b">Cost Name</th>
                  <th className="px-3 py-2 text-left border-b">Markup Type</th>
                  <th className="px-3 py-2 text-left border-b">Markup Amount</th>
                  <th className="px-3 py-2 text-left border-b">
                    Single Occupancy Supplement Cost
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Adult Extra Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Child Extra Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    Cost-Per Child W/o Bed
                  </th>
                  <th className="px-3 py-2 text-left border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {standardPricing2.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="px-3 py-2">{row.costName}</td>
                    <td className="px-3 py-2">{row.markupType}</td>
                    <td className="px-3 py-2">{row.markupAmount}</td>
                    <td className="px-3 py-2">
                      {row.singleOccupancySupplementCost}
                    </td>
                    <td className="px-3 py-2">{row.costPerAdultExtraBed}</td>
                    <td className="px-3 py-2">{row.costPerChildExtraBed}</td>
                    <td className="px-3 py-2">{row.costPerChildWoBed}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Edit"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                          title="Add"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricePaymentInformation;

